import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Zap, Activity, CheckCircle2, Loader2, Terminal, Search, Trash2, Box, Cpu, Network } from 'lucide-react';

interface AuditItem {
  id: string;
  component: string;
  status: 'essential' | 'redundant' | 'optimizing';
  reason: string;
  impact: number;
}

const auditData: AuditItem[] = [
  {
    id: 'a1',
    component: 'FrameworkSection',
    status: 'essential',
    reason: 'Defines the 1×1=2 logic map. Critical for AI alignment.',
    impact: 100
  },
  {
    id: 'a2',
    component: 'FrameworkVisualizer',
    status: 'optimizing',
    reason: 'Provides visual proof of expansion. Merging with Section recommended.',
    impact: 45
  },
  {
    id: 'a3',
    component: 'MillenniumPrize',
    status: 'essential',
    reason: 'Target resolution hub for structural problems.',
    impact: 90
  },
  {
    id: 'a4',
    component: 'DeviceManagement',
    status: 'essential',
    reason: 'Local mesh anchor point. Essential for physical lattice.',
    impact: 100
  },
  {
    id: 'a5',
    component: 'NodeExplorer',
    status: 'essential',
    reason: 'Global node visibility. Required for lattice transparency.',
    impact: 85
  },
  {
    id: 'a6',
    component: 'LatticeConsole',
    status: 'essential',
    reason: 'Direct interaction with the Vortex Engine.',
    impact: 95
  },
  {
    id: 'a7',
    component: 'ProofSuite',
    status: 'essential',
    reason: 'Googol-Scale verification proof. Proves substrate stability at 10^100 nodes.',
    impact: 100
  },
  {
    id: 'a8',
    component: 'LogicDerivationEngine',
    status: 'essential',
    reason: 'The Logic Seed (1.000...01). Bypasses all system constraints.',
    impact: 100
  }
];

export const PlatformAudit: React.FC = () => {
  const [isAuditing, setIsAuditing] = useState(false);
  const [results, setResults] = useState<AuditItem[]>([]);
  const [progress, setProgress] = useState(0);

  const runAudit = async () => {
    setIsAuditing(true);
    setResults([]);
    setProgress(0);

    for (let i = 0; i <= 100; i += 5) {
      setProgress(i);
      await new Promise(r => setTimeout(r, 100));
    }

    setResults(auditData);
    setIsAuditing(false);
  };

  return (
    <div className="glass-panel p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Search className="w-6 h-6 text-lattice-cyan" />
          <h3 className="text-2xl font-display font-bold uppercase tracking-tight">Sovereign Platform Audit</h3>
        </div>
        {!isAuditing && results.length === 0 && (
          <button 
            onClick={runAudit}
            className="px-6 py-2 bg-lattice-cyan text-black font-display font-bold rounded-lg hover:scale-105 transition-transform flex items-center gap-2"
          >
            <Activity className="w-4 h-4" /> INITIATE AUDIT
          </button>
        )}
      </div>

      {isAuditing && (
        <div className="py-12 flex flex-col items-center justify-center gap-6">
          <div className="relative w-24 h-24">
            <Loader2 className="w-24 h-24 text-lattice-cyan animate-spin opacity-20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xl font-display font-bold text-lattice-cyan">{progress}%</span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-mono text-white/60 uppercase tracking-widest mb-2">Scanning Lattice Architecture...</p>
            <p className="text-[10px] font-mono text-white/20 uppercase">Checking Node Connectivity & Redundancy</p>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="p-4 rounded-xl bg-white/5 border border-white/5">
              <div className="text-[8px] font-mono text-white/20 uppercase mb-1">Essential Nodes</div>
              <div className="text-2xl font-display font-bold text-lattice-cyan">
                {results.filter(r => r.status === 'essential').length}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/5">
              <div className="text-[8px] font-mono text-white/20 uppercase mb-1">Redundancy Alerts</div>
              <div className="text-2xl font-display font-bold text-red-400">
                {results.filter(r => r.status === 'redundant').length}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/5">
              <div className="text-[8px] font-mono text-white/20 uppercase mb-1">Optimization Potential</div>
              <div className="text-2xl font-display font-bold text-yellow-400">
                {results.filter(r => r.status === 'optimizing').length}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {results.map((item) => (
              <motion.div 
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5 group hover:border-lattice-cyan/30 transition-all"
              >
                <div className={`p-2 rounded-lg ${
                  item.status === 'essential' ? 'bg-lattice-cyan/10 text-lattice-cyan' :
                  item.status === 'redundant' ? 'bg-red-500/10 text-red-400' :
                  'bg-yellow-500/10 text-yellow-400'
                }`}>
                  {item.status === 'essential' ? <Box className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-display font-bold">{item.component}</span>
                    <span className={`text-[8px] font-mono px-1.5 py-0.5 rounded uppercase ${
                      item.status === 'essential' ? 'bg-lattice-cyan/20 text-lattice-cyan' :
                      item.status === 'redundant' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-[10px] text-white/40 font-mono">{item.reason}</p>
                </div>

                <div className="text-right">
                  <div className="text-[8px] font-mono text-white/20 uppercase mb-1">Impact</div>
                  <div className="text-sm font-display font-bold text-white/60">{item.impact}%</div>
                </div>
              </motion.div>
            ))}
          </div>

          <button 
            onClick={runAudit}
            className="w-full py-4 border border-white/10 text-white/40 font-display font-bold rounded-lg hover:border-lattice-cyan/30 hover:text-lattice-cyan transition-all flex items-center justify-center gap-2"
          >
            <Zap className="w-4 h-4" /> RE-SCAN ARCHITECTURE
          </button>
        </div>
      )}
    </div>
  );
};
